import { Upload, X, Phone, MessageSquare, EyeOff, Check, Star } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { resolveImageUrl } from "@/lib/api";

const BER_RATINGS = ["A1", "A2", "A3", "B1", "B2", "B3", "C1", "C2", "C3", "D1", "D2", "E1", "E2", "F", "G", "Exempt"];

export function CategoryStep({ cats, form, set }) {
  const subList = form.category && cats[form.category] ? cats[form.category].subcategories : [];
  return (
    <div className="space-y-5" data-testid="step-category">
      <div>
        <Label>Category</Label>
        <div className="grid grid-cols-2 gap-3 mt-2">
          {Object.keys(cats).map((c) => (
            <button key={c} type="button" data-testid={`cat-option-${c}`} onClick={() => { set("category", c); set("subcategory", ""); }}
              className={`p-3 border rounded-md text-sm font-medium transition-colors ${form.category === c ? "border-emerald-600 bg-emerald-50 text-emerald-700" : "border-slate-200 hover:border-slate-300"}`}>{c}</button>
          ))}
        </div>
      </div>
      {subList.length > 0 && (
        <div>
          <Label>Sub-category</Label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
            {subList.map((s) => (
              <button key={s} type="button" data-testid={`subcat-option-${s}`} onClick={() => set("subcategory", s)}
                className={`p-2.5 border rounded-md text-sm transition-colors ${form.subcategory === s ? "border-emerald-600 bg-emerald-50 text-emerald-700" : "border-slate-200 hover:border-slate-300"}`}>{s}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function PhotosStep({ form, uploading, onFiles, removeImg }) {
  return (
    <div className="space-y-4" data-testid="step-photos">
      <Label>Photos (up to 6)</Label>
      <label className="block border-2 border-dashed border-slate-300 rounded-lg p-8 text-center cursor-pointer hover:border-emerald-400 transition-colors" data-testid="upload-zone">
        <Upload className="mx-auto text-slate-400 mb-2" />
        <p className="text-sm text-slate-500">{uploading ? "Uploading…" : "Click to upload product photos"}</p>
        <input type="file" accept="image/*" multiple disabled={uploading || form.images.length >= 6} className="hidden" onChange={onFiles} data-testid="file-input" />
      </label>
      {form.images.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          {form.images.map((img, i) => (
            <div key={img} className="relative aspect-square rounded-md overflow-hidden border border-slate-200">
              <img src={resolveImageUrl(img)} alt="" className="w-full h-full object-cover" />
              <button type="button" onClick={() => removeImg(i)} className="absolute top-1 right-1 bg-slate-900/70 text-white rounded-full p-1"><X size={14} /></button>
            </div>
          ))}
        </div>
      )}
      <p className="text-xs text-slate-400">Photos are optional but ads with photos sell faster.</p>
    </div>
  );
}

export function DetailsStep({ form, set, setSpec, counties }) {
  const isProperty = ["Lettings", "Property for Sale"].includes(form.category);
  const isLettings = form.category === "Lettings";
  return (
    <div className="space-y-4" data-testid="step-details">
      <div><Label>Title</Label><Input data-testid="ad-title" className="mt-1" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. 2019 Volkswagen Golf" /></div>
      <div className="grid grid-cols-2 gap-4">
        <div><Label>{isLettings ? "Price (€ / month)" : "Price (€)"}</Label><Input data-testid="ad-price" type="number" className="mt-1" value={form.price} onChange={(e) => set("price", e.target.value)} placeholder="0" /></div>
        <div>
          <Label>Condition</Label>
          <Select value={form.condition} onValueChange={(v) => set("condition", v)}>
            <SelectTrigger data-testid="ad-condition" className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="New">New</SelectItem><SelectItem value="Used">Used</SelectItem></SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label>County</Label>
        <Select value={form.county} onValueChange={(v) => set("county", v)}>
          <SelectTrigger data-testid="ad-county" className="mt-1"><SelectValue placeholder="Select county" /></SelectTrigger>
          <SelectContent className="max-h-64">{counties.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div><Label>Area / Town</Label><Input data-testid="ad-area" className="mt-1" value={form.area || ""} onChange={(e) => set("area", e.target.value)} placeholder="e.g. Swords" /></div>
        <div><Label>Eircode <span className="text-slate-400 font-normal">(optional)</span></Label><Input data-testid="ad-eircode" className="mt-1" value={form.eircode || ""} onChange={(e) => set("eircode", e.target.value)} placeholder="e.g. D01 F5P2" /></div>
      </div>
      {isProperty && (
        <div className="grid grid-cols-2 gap-4 rounded-md bg-slate-50 border border-slate-200 p-4" data-testid="property-fields">
          <div><Label>Bedrooms</Label><Input data-testid="prop-bedrooms" type="number" min="0" className="mt-1" value={form.specs?.Bedrooms || ""} onChange={(e) => setSpec("Bedrooms", e.target.value)} placeholder="e.g. 3" /></div>
          <div><Label>Bathrooms</Label><Input data-testid="prop-bathrooms" type="number" min="0" className="mt-1" value={form.specs?.Bathrooms || ""} onChange={(e) => setSpec("Bathrooms", e.target.value)} placeholder="e.g. 2" /></div>
          <div>
            <Label>BER rating</Label>
            <Select value={form.specs?.BER || ""} onValueChange={(v) => setSpec("BER", v)}>
              <SelectTrigger data-testid="prop-ber" className="mt-1"><SelectValue placeholder="Select BER" /></SelectTrigger>
              <SelectContent className="max-h-64">{BER_RATINGS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {isLettings && (
            <div>
              <Label>Furnished</Label>
              <Select value={form.specs?.Furnished || ""} onValueChange={(v) => setSpec("Furnished", v)}>
                <SelectTrigger data-testid="prop-furnished" className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Furnished">Furnished</SelectItem>
                  <SelectItem value="Part-furnished">Part-furnished</SelectItem>
                  <SelectItem value="Unfurnished">Unfurnished</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      )}
      <div><Label>Description</Label><Textarea data-testid="ad-description" className="mt-1" rows={5} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Describe your item..." /></div>
    </div>
  );
}

export function ContactStep({ form, set, fees, freeAvailable, total, isEditing }) {
  const options = [
    { v: "show_phone", icon: Phone, t: "Show my phone number", d: "Buyers can call or message you" },
    { v: "in_app", icon: MessageSquare, t: "In-app messaging only", d: "Keep your number private" },
    { v: "hide", icon: EyeOff, t: "Hide number", d: "Only show messaging button" },
  ];
  return (
    <div className="space-y-4" data-testid="step-contact">
      <Label>How should buyers contact you?</Label>
      {options.map((o) => (
        <button key={o.v} type="button" data-testid={`contact-${o.v}`} onClick={() => set("contact_option", o.v)}
          className={`w-full flex items-start gap-3 p-4 border rounded-md text-left transition-colors ${form.contact_option === o.v ? "border-emerald-600 bg-emerald-50" : "border-slate-200 hover:border-slate-300"}`}>
          <o.icon size={20} className={form.contact_option === o.v ? "text-emerald-600" : "text-slate-400"} />
          <div><p className="font-medium text-slate-900">{o.t}</p><p className="text-sm text-slate-500">{o.d}</p></div>
        </button>
      ))}
      {form.contact_option === "show_phone" && (
        <div><Label>Phone number</Label><Input data-testid="ad-phone" className="mt-1" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+353 87 123 4567" /></div>
      )}
      <button type="button" data-testid="spotlight-boost" onClick={() => set("premium", !form.premium)}
        className={`w-full flex items-start gap-3 p-4 mt-2 border rounded-md text-left transition-colors ${form.premium ? "border-amber-400 bg-amber-50" : "border-slate-200 hover:border-slate-300"}`}>
        <Star size={20} className={form.premium ? "text-amber-500" : "text-slate-400"} fill={form.premium ? "#f59e0b" : "none"} />
        <div className="flex-1">
          <p className="font-medium text-slate-900">Spotlight Boost <span className="text-amber-600">+€{fees.spotlight_fee}</span></p>
          <p className="text-sm text-slate-500">Show your ad at the top of the homepage and search results, where it gets up to 5 times more views.</p>
        </div>
        <span className={`shrink-0 w-5 h-5 rounded border grid place-items-center ${form.premium ? "bg-amber-500 border-amber-500" : "border-slate-300"}`}>{form.premium && <Check size={14} className="text-white" />}</span>
      </button>
      {!isEditing && (
        <div className="mt-2 rounded-md bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-800" data-testid="fee-notice">
          <div className="flex items-center justify-between">
            <span>Listing fee {freeAvailable && <span className="ml-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-600">first ad free</span>}</span>
            {freeAvailable ? (
              <span><span className="line-through text-emerald-500/70 mr-1">€{fees.listing_fee.toFixed(2)}</span><span className="font-semibold">FREE</span></span>
            ) : (
              <span>€{fees.listing_fee.toFixed(2)}</span>
            )}
          </div>
          {form.premium && <div className="flex items-center justify-between mt-1"><span>Spotlight boost</span><span>€{fees.spotlight_fee.toFixed(2)}</span></div>}
          <div className="flex items-center justify-between font-semibold border-t border-emerald-200 mt-2 pt-2"><span>Total due</span><span data-testid="fee-total">€{total.toFixed(2)}</span></div>
        </div>
      )}
    </div>
  );
}
